import React, { Fragment, useContext } from 'react';
import { Summary as OrderSummary, StepName } from '|/app/services';
import * as Summary from '|/app/steps/order-summary/order-summary-components.styles';
import currency from '|/helpers/currency';
import percent from '|/helpers/percent';
import useOrderSummaryVm from '|/helpers/use-order-summary';
import SummaryContext from '../../../../summary-context';
import useNavigateToStep from '../../../use-navigate-to-step';

const defaultProps = {
  summary: {} as OrderSummary
};

const PaymentSection = ({ summary } = defaultProps) => {
  const navigateToStep = useNavigateToStep();
  const { setShowSummary } = useContext(SummaryContext);
  const {
    isCarvanaFinancing,
    isOutsideFinance,
    isCash,
    isThirdParty,
    hasPaymentMethodBeenSelected,
    hasTerms,
    fundingInstitution: institution,
    lenderName,
    amountFinanced,
    cashDown,
    loanApr: apr,
    loanTerm: term,
    loanPayment: payment,
    thirdPartyLoanAmount: loanAmount,
    enrolledInAutopay
  } = useOrderSummaryVm(summary);

  return (
    <Summary.Section>
      <Summary.Heading>Payment Method &amp; Terms</Summary.Heading>
      {!hasPaymentMethodBeenSelected && (
        <Summary.Box>
          <Summary.BoxText>
            * We still need your payment method &amp; terms
          </Summary.BoxText>
        </Summary.Box>
      )}

      {hasPaymentMethodBeenSelected && (
        <Summary.Box>
          <Summary.List>
            <Summary.SummaryListItem
              label="Payment Method"
              value={`
              ${isCarvanaFinancing ? 'Finance with Carvana' : ''}
              ${isCash ? 'Cash Payment' : ''}
              ${isOutsideFinance ? `${institution} Financing` : ''}
              ${isThirdParty ? `${lenderName || 'Third Party'} Financing` : ''}
            `}
            />

            {(isCarvanaFinancing || isOutsideFinance) && hasTerms && (
              <Fragment>
                <Summary.SummaryListItem
                  label="Loan Amount"
                  value={currency(amountFinanced || 0)}
                />
                <Summary.SummaryListItem
                  label="Cash down payment"
                  value={currency(cashDown || 0)}
                />

                {!!(apr && apr > 0) && (
                  <Summary.SummaryListItem
                    label="APR (Interest rate)"
                    value={percent(apr)}
                  />
                )}

                {!!(term && term > 0) && (
                  <Summary.SummaryListItem
                    label="Loan length"
                    value={`${term.toString()} months`}
                  />
                )}

                {!!(payment && payment > 0) && (
                  <Summary.SummaryListItem
                    label="Monthly payment"
                    value={currency(payment)}
                  />
                )}
              </Fragment>
            )}

            {isThirdParty && (
              <Fragment>
                <Summary.SummaryListItem
                  label="Loan Amount"
                  value={loanAmount ? currency(loanAmount) : ''}
                />
              </Fragment>
            )}
          </Summary.List>
          {hasPaymentMethodBeenSelected && (
            <Summary.EditButton
              onClick={() => {
                setShowSummary(false);
                navigateToStep(StepName.CoF);
              }}
              data-analytics-id="pp_order_summary_edit_payment"
            >
              Edit Payment Details
            </Summary.EditButton>
          )}
        </Summary.Box>
      )}

      {isCarvanaFinancing && (
        <>
          <Summary.Heading>Autopay</Summary.Heading>
          <Summary.Box>
            <Summary.SummaryListItem
              label="Autopay"
              value={enrolledInAutopay ? 'Enrolled' : 'Not Enrolled'}
            />
            {enrolledInAutopay !== null && (
              <Summary.EditButton
                onClick={() => {
                  setShowSummary(false);
                  navigateToStep(StepName.Autopay);
                }}
                data-analytics-id="pp_order_summary_edit_autopay"
              >
                Edit Autopay
              </Summary.EditButton>
            )}
          </Summary.Box>
        </>
      )}
    </Summary.Section>
  );
};

export default PaymentSection;
